import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { BankInfoSetupFieldsPanel } from "./Panels/BankSetupFieldsPanel";
import { BankService } from "../../services/BankService";
import { BankSetupToolbar } from "./BankSetupToolbar/BankSetupToolbar";
import { BankSetupValidationSchema } from "./validator";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IBank } from "../../models/Bank/IBank";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";









export interface IBankSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "bank-single-data-record";
const BANK_LIST_DATA_QUERY_KEY = "bank-list";

const useGetData = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			return BankService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useBank = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [BANK_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {
			return BankService.getBank(params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

export const BankSetup: React.FC<IBankSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	// const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [bankId, setBankId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();
	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "bankName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "bankName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};
		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useBank(queryParams, navigateToLogout);

	const bankList: any | null = data;

	const { isPending: isSubmitting, mutate: submitBankFormFields } = useMutation(
		{
			mutationFn: BankService.create,
			onSuccess: (res: IBank | IServerError | IResponse | any) => {
				if (res.status === 200) {
					// if (instanceOfIBankResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/bank/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [BANK_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIBankResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/bank/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [BANK_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		}
	);

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		editRecordId,
		navigateToLogout
	);

	const upadtedBankListItems = useMemo(() => {
		return (
			bankList &&
			bankList.items &&
			bankList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/bank/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.bankName}
					</Link>
				),
			}))
		);
	}, [bankList]);

	const DEFAULT_VALUES = {
		bankCode: undefined,
		bankName: undefined,
		bankDomain: undefined

	};
	const formMethods = useForm<any>({
		resolver: yupResolver(BankSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {
		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			bankCode: values.bankCode,
			bankDomain: values.bankDomain,
			bankName: values.bankName,
		};
		submitBankFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteBankById } = useMutation({
		mutationFn: BankService.delBank,
		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Bank fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/bank`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [BANK_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteBankField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				bankId: bankId,
				navigateToLogout: navigateToLogout,
			};
			deleteBankById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setBankId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setBankId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveBankById } = useMutation({
		mutationFn: BankService.archiveBank,
		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Bank fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/bank`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [BANK_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveBankField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				bankId: bankId,
				navigateToLogout: navigateToLogout,
			};
			archiveBankById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setBankId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/bank`;
		navigate(path);
	};

	const expandedLabels = ["Misc.", "Bank"];
	const expandedBankSetupOrSetupItem = "Bank"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc." &&
				item.label === expandedBankSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedBankSetupOrSetupItem && "/bank",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		bankName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IBank | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/bank/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteBankField}
				cancelActionHandler={onCloseHandler}
				testId="bank-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Bank"
										testId={`bank`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<BankInfoSetupFieldsPanel />
									</StandardPanel>
								}
								toolbar={
									<BankSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								toolbarPosition="bottom"
								showFullDiaplay={true}
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 3, lg: 3, xl: 3 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5.5, lg: 5.5, xl: 5.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									bankList && upadtedBankListItems ? upadtedBankListItems : []
								}
								columns={[
									{ field: "label", headerName: "Bank Name" },
									{ field: "bankCode", headerName: "Bank Code" },
									{ field: "bankDomain", headerName: "Bank Domain" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={bankList && bankList.total ? bankList.total : 0}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				// showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveBankField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"bank-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
